import React, { PropsWithChildren, useMemo } from 'react'

import { Config } from '@flowwai-frontend/shared/constants'
import { FullScreenLoader } from '@flowwai-frontend/shared/common-components'
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings'

import { getDynamicFeatureTogglesTier } from '../../utils'
import { resolveCaptchaGateScope } from '../../utils/captchaGateScopeUtils'
import { CaptchaGateScopeProvider } from '../../contexts/CaptchaGateScopeContext'
import { FeatureToggleAppId } from '../../types/CaptchaGateTypes'

interface Props extends PropsWithChildren {
   /**
    * Compile-time identity of the mounting app. Required: it is the only
    * signal that separates officer from admin (identical client enum) and it
    * cannot drift the way an env var could.
    */
   appId: FeatureToggleAppId
}

const DynamicFeatureTogglesProvider = (props: Props): React.ReactElement => {
   const { children, appId } = props

   const token = Config.STATS_IG_SECRET_TOKEN

   // Scope is fixed for the process lifetime (each app is one build), so
   // resolve it once. Provided on both branches so the hook never reads Config.
   const scope = useMemo(
      () =>
         resolveCaptchaGateScope({
            appId,
            clientEnum: Config.APP_CLIENT_ENUM,
            portalType: Config.PORTAL_TYPE,
            portalSubType: Config.PORTAL_SUB_TYPE
         }),
      [appId]
   )

   const { client } = useClientAsyncInit(
      token,
      {
         //NOTE: need to set user id on login and logout, which will be done later
      },
      {
         environment: {
            tier: getDynamicFeatureTogglesTier()
         }
      }
   )

   if (!token) {
      return (
         <CaptchaGateScopeProvider scope={scope}>
            {children}
         </CaptchaGateScopeProvider>
      )
   }

   return (
      <CaptchaGateScopeProvider scope={scope}>
         <StatsigProvider
            client={client}
            loadingComponent={<FullScreenLoader />}
         >
            {children}
         </StatsigProvider>
      </CaptchaGateScopeProvider>
   )
}

export default DynamicFeatureTogglesProvider
